import { call } from '@advanza/api'
import { InputField } from '@advanza/input'
import Checkbox from '@advanza/input/Checkbox'
import Button from '@advanza/ui/Button'
import Card from 'components/Card'
import Page from 'components/ui/Page'
import { useEffect, useMemo, useState } from 'react'

interface Redirect {
    source: string
    destination: string
    permanent: boolean
}

function parseCsvInput(text: string): { source: string; destination: string }[] {
    return text
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
            // Support comma, semicolon, or tab as delimiter
            const [source, destination] = line.split(/[,;\t]/).map((s) => s.trim())
            return { source: source || '', destination: destination || '' }
        })
        .filter((r) => r.source && r.destination)
}

const RedirectsPage = () => {
    const [redirects, setRedirects] = useState<Redirect[]>([])
    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState('')

    // Form state
    const [csvInput, setCsvInput] = useState('')
    const [permanent, setPermanent] = useState(true)
    const [saving, setSaving] = useState(false)
    const [saveProgress, setSaveProgress] = useState('')
    const [errors, setErrors] = useState<string[]>([])

    const parsed = useMemo(() => parseCsvInput(csvInput), [csvInput])

    const fetchRedirects = () => {
        setLoading(true)
        call('office/redirects/list-redirects')
            .then((res) => {
                setRedirects(res.redirects || [])
            })
            .catch(() => {})
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        fetchRedirects()
    }, [])

    const handleSave = async () => {
        if (parsed.length === 0) return
        setSaving(true)
        setErrors([])
        setSaveProgress(`Saving ${parsed.length} redirect${parsed.length === 1 ? '' : 's'}...`)

        try {
            await call('office/redirects/save-redirects', {
                json: { items: parsed.map((e) => ({ ...e, permanent })) },
            })
            setCsvInput('')
            setErrors([])
        } catch (e: any) {
            const msg = e?.error || e?.message || 'Unknown error'
            setErrors(msg.split('\n').filter(Boolean))
        }

        setSaveProgress('')
        setSaving(false)
        fetchRedirects()
    }

    const handleDelete = (redirectSource: string) => {
        if (!window.confirm(`Delete redirect for "${redirectSource}"?`)) return
        call('office/redirects/delete-redirect', {
            json: { source: redirectSource },
        })
            .then(() => fetchRedirects())
            .catch(() => {})
    }

    const handleEdit = (redirect: Redirect) => {
        setCsvInput(`${redirect.source},${redirect.destination}`)
        setPermanent(redirect.permanent)
    }

    const filtered = redirects.filter(
        (r) =>
            r.source.toLowerCase().includes(search.toLowerCase()) ||
            r.destination.toLowerCase().includes(search.toLowerCase()),
    )

    return (
        <Page title="Redirects">
            <div className="m-auto max-w-screen-xl my-16 space-y-8">
                <h1>Redirect Management</h1>
                <Card header="Add Redirects">
                    <div className="flex flex-col gap-4">
                        <textarea
                            className="w-full border rounded p-3 font-mono text-sm"
                            rows={10}
                            style={{ minHeight: '185px' }}
                            placeholder={
                                '/old-page,/new-page\n/another-old,/another-new\n\nPaste one redirect per line: source,destination'
                            }
                            value={csvInput}
                            onChange={(e) => setCsvInput(e.target.value)}
                        />
                        {parsed.length > 0 && (
                            <div className="border rounded p-3 bg-neutral-50">
                                <div className="text-sm text-neutral-500 mb-2">
                                    {parsed.length} redirect{parsed.length === 1 ? '' : 's'} parsed
                                </div>
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b text-left text-neutral-500">
                                            <th className="py-1 pr-4">Source</th>
                                            <th className="py-1">Destination</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {parsed.map((r, i) => (
                                            <tr key={i} className="border-b last:border-0">
                                                <td className="py-1 pr-4 font-mono">{r.source}</td>
                                                <td className="py-1 font-mono">{r.destination}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <div className="flex gap-4 items-center">
                            <Checkbox
                                value={permanent}
                                onChange={() => setPermanent(!permanent)}
                                title="Permanent (301)"
                            />
                            <Button onClick={handleSave} disabled={saving || parsed.length === 0}>
                                {saving
                                    ? saveProgress
                                    : `Save ${parsed.length || ''} redirect${parsed.length === 1 ? '' : 's'}`}
                            </Button>
                        </div>
                        {errors.length > 0 && (
                            <div className="bg-red-50 border border-red-200 rounded p-3 text-md text-red-800">
                                {errors.map((err, i) => (
                                    <div key={i}>{err}</div>
                                ))}
                            </div>
                        )}
                    </div>
                </Card>
                <Card header={`Redirects (${filtered.length})`} isLoading={loading}>
                    <div className="mb-4">
                        <InputField
                            placeholder="Search redirects..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <table className="w-full text-md">
                        <thead>
                            <tr className="border-b text-left">
                                <th className="py-2 pr-4">Source</th>
                                <th className="py-2 pr-4">Destination</th>
                                <th className="py-2 pr-4">Type</th>
                                <th className="py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => (
                                <tr key={r.source} className="border-b hover:bg-neutral-50">
                                    <td className="py-2 pr-4 font-mono text-sm">{r.source}</td>
                                    <td className="py-2 pr-4 font-mono text-sm">{r.destination}</td>
                                    <td className="py-2 pr-4">{r.permanent ? '301' : '302'}</td>
                                    <td className="py-2 flex gap-2">
                                        <button
                                            className="text-blue-600 hover:underline text-sm"
                                            onClick={() => handleEdit(r)}>
                                            Edit
                                        </button>
                                        <button
                                            className="text-red-600 hover:underline text-sm"
                                            onClick={() => handleDelete(r.source)}>
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={4} className="py-4 text-center text-neutral-500">
                                        {search
                                            ? 'No redirects match your search'
                                            : 'No redirects yet'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </Card>
            </div>
        </Page>
    )
}

export default RedirectsPage
